import React, { useState } from 'react';
import { type LayerConfig } from '../../../types/metadata';
import { useMapStore } from '../store/useMapStore';
import { Popup } from './Popup';
import { MarkerLayerConfig } from './MarkerLayerConfig';
import { HeatmapLayerConfig } from './HeatmapLayerConfig';
import { ViewLayerConfig } from './ViewLayerConfig';
import { metadataService } from '../../../services/metadataService';
import { DEFAULT_POINT_SETTINGS } from '../constants/pointSettings';
import { PointSettings, ViewSettings } from '../../../types/metadata';
import './PopupColorPicker.css';

interface LayerSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  layer: LayerConfig;
}

type Section = 'marker' | 'heatmap' | 'view';

export const LayerSettingsModal: React.FC<LayerSettingsModalProps> = ({ isOpen, onClose, layer }) => {
  const updateLayerConfig = useMapStore((state) => state.updateLayerConfig);
  const layerConfig = useMapStore((state) => state.layersConfig[layer.id]);
  const [activeSection, setActiveSection] = useState<Section>('marker');
  const [isSavingDefault, setIsSavingDefault] = useState(false);
  const [saveCounter, setSaveCounter] = useState(0); // Force re-render when settings are saved
  
  // Store the original server settings
  const originalServerSettingsRef = React.useRef<Partial<PointSettings> | null>(null);
  const originalViewSettingsRef = React.useRef<ViewSettings | null | undefined>(undefined);
  
  // Initialize on first render or when layer changes
  if (originalServerSettingsRef.current === null) {
    originalServerSettingsRef.current = layerConfig?.pointSettings ? { ...layerConfig.pointSettings } : {};
  }
  
  if (originalViewSettingsRef.current === undefined) {
    originalViewSettingsRef.current = layerConfig?.viewSettings ? { ...layerConfig.viewSettings } : null;
  }
  
  // Get current resolved settings (with defaults applied)
  const settings = React.useMemo(() => {
    return { ...DEFAULT_POINT_SETTINGS, ...layerConfig?.pointSettings };
  }, [layerConfig?.pointSettings]);
  
  // Check if marker settings differ from server defaults (only fields editable in Marker section)
  const hasMarkerSettingsChanged = React.useMemo(() => {
    const serverSettings = originalServerSettingsRef.current || {};
    const currentSettings = layerConfig?.pointSettings || {};
    
    const isDifferent = (current: any, server: any) => {
      if (current == null && server == null) return false;
      if (current == null || server == null) return true;
      return JSON.stringify(current) !== JSON.stringify(server);
    };
    
    // Marker section fields: markerType, color, size, borderColor, borderSize, opacity, svg
    const markerTypeChanged = currentSettings.markerType !== undefined && isDifferent(currentSettings.markerType, serverSettings.markerType);
    const colorChanged = currentSettings.color !== undefined && isDifferent(currentSettings.color, serverSettings.color);
    const sizeChanged = currentSettings.size !== undefined && isDifferent(currentSettings.size, serverSettings.size);
    const borderColorChanged = currentSettings.borderColor !== undefined && isDifferent(currentSettings.borderColor, serverSettings.borderColor);
    const borderSizeChanged = currentSettings.borderSize !== undefined && isDifferent(currentSettings.borderSize, serverSettings.borderSize);
    const opacityChanged = currentSettings.opacity !== undefined && isDifferent(currentSettings.opacity, serverSettings.opacity);
    const svgChanged = currentSettings.svg !== undefined && isDifferent(currentSettings.svg, serverSettings.svg);
    
    return markerTypeChanged || colorChanged || sizeChanged || borderColorChanged || borderSizeChanged || opacityChanged || svgChanged;
  }, [layerConfig?.pointSettings]);
  
  // Check if heatmap settings differ from server defaults (only fields editable in Heatmap section)
  const hasHeatmapSettingsChanged = React.useMemo(() => {
    const serverSettings = originalServerSettingsRef.current || {};
    const currentSettings = layerConfig?.pointSettings || {};
    
    const isDifferent = (current: any, server: any) => {
      if (current == null && server == null) return false;
      if (current == null || server == null) return true;
      return JSON.stringify(current) !== JSON.stringify(server);
    };
    
    // Heatmap section fields: heatmapThreshold, heatmapType, heatmapPrimaryColor, heatmapSecondaryColor, heatmapStrokeColor, heatmapStroke, heatmapOpacity
    const heatmapThresholdChanged = currentSettings.heatmapThreshold !== undefined && isDifferent(currentSettings.heatmapThreshold, serverSettings.heatmapThreshold);
    const heatmapTypeChanged = currentSettings.heatmapType !== undefined && isDifferent(currentSettings.heatmapType, serverSettings.heatmapType);
    const heatmapPrimaryColorChanged = currentSettings.heatmapPrimaryColor !== undefined && isDifferent(currentSettings.heatmapPrimaryColor, serverSettings.heatmapPrimaryColor);
    const heatmapSecondaryColorChanged = currentSettings.heatmapSecondaryColor !== undefined && isDifferent(currentSettings.heatmapSecondaryColor, serverSettings.heatmapSecondaryColor);
    const heatmapStrokeColorChanged = currentSettings.heatmapStrokeColor !== undefined && isDifferent(currentSettings.heatmapStrokeColor, serverSettings.heatmapStrokeColor);
    const heatmapStrokeChanged = currentSettings.heatmapStroke !== undefined && isDifferent(currentSettings.heatmapStroke, serverSettings.heatmapStroke);
    const heatmapOpacityChanged = currentSettings.heatmapOpacity !== undefined && isDifferent(currentSettings.heatmapOpacity, serverSettings.heatmapOpacity);
    
    return heatmapThresholdChanged || heatmapTypeChanged || heatmapPrimaryColorChanged || heatmapSecondaryColorChanged || heatmapStrokeColorChanged || heatmapStrokeChanged || heatmapOpacityChanged;
  }, [layerConfig?.pointSettings]);

  // Check if view settings differ from server defaults
  const hasViewSettingsChanged = React.useMemo(() => {
    const serverSettings = originalViewSettingsRef.current;
    const currentSettings = layerConfig?.viewSettings;
    
    // Both null/undefined - no change
    if (serverSettings == null && currentSettings == null) {
      return false;
    }
    
    // One is null/undefined and the other isn't - changed
    if ((serverSettings == null) !== (currentSettings == null)) {
      return true;
    }
    
    // Both have values - compare them
    return JSON.stringify(currentSettings) !== JSON.stringify(serverSettings);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerConfig?.viewSettings, saveCounter]); // Include saveCounter to force recalculation

  const handleSetAsDefault = async () => {
    setIsSavingDefault(true);
    try {
      await metadataService.updateSettings({
        id: layer.id,
        pointSettings: settings,
      });
      
      updateLayerConfig(layer.id, {
        pointSettings: settings,
      });
      
      // Update the reference to the new saved settings
      originalServerSettingsRef.current = { ...settings };
      
      console.log('Successfully updated default settings for layer:', layer.name);
    } catch (error) {
      console.error('Failed to update default settings:', error);
      alert('Failed to save default settings. Please try again.');
    } finally {
      setIsSavingDefault(false);
    }
  };

  const handleSetViewAsDefault = async () => {
    setIsSavingDefault(true);
    try {
      await metadataService.updateSettings({
        id: layer.id,
        viewSettings: layerConfig?.viewSettings || null,
      });
      
      updateLayerConfig(layer.id, {
        viewSettings: layerConfig?.viewSettings,
      });
      
      // Update the reference to the new saved settings (keep undefined as undefined, not null)
      originalViewSettingsRef.current = layerConfig?.viewSettings ? { ...layerConfig.viewSettings } : layerConfig?.viewSettings;
      
      // Force re-render to update the button visibility
      setSaveCounter(prev => prev + 1);
      
      console.log('Successfully updated default view settings for layer:', layer.name);
    } catch (error) {
      console.error('Failed to update default view settings:', error);
      alert('Failed to save default view settings. Please try again.');
    } finally {
      setIsSavingDefault(false);
    }
  };

  return (
    <Popup isOpen={isOpen} onClose={onClose} className="popup-color-picker">
      <div className="popup-layout">
        <div className="popup-top-bar">
          <h2>Layer Settings</h2>
          <button className="popup-close-btn" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="popup-main">
          <div className="popup-sidebar">
            <nav className="popup-nav">
              <div className={`nav-item-wrapper ${activeSection === 'marker' ? 'active' : ''}`}>
                <button
                  className="nav-item"
                  onClick={() => setActiveSection('marker')}
                >
                  Marker
                </button>
                {hasMarkerSettingsChanged && (
                  <button 
                    className="set-default-btn-sidebar"
                    onClick={handleSetAsDefault}
                    disabled={isSavingDefault}
                    title="Save current settings as default for this layer"
                  >
                    {isSavingDefault ? 'saving...' : 'set as default'}
                  </button>
                )}
              </div>
              <div className={`nav-item-wrapper ${activeSection === 'heatmap' ? 'active' : ''}`}>
                <button
                  className="nav-item"
                  onClick={() => setActiveSection('heatmap')}
                >
                  Heatmap
                </button>
                {hasHeatmapSettingsChanged && (
                  <button 
                    className="set-default-btn-sidebar"
                    onClick={handleSetAsDefault}
                    disabled={isSavingDefault}
                    title="Save current settings as default for this layer"
                  >
                    {isSavingDefault ? 'saving...' : 'set as default'}
                  </button>
                )}
              </div>
              <div className={`nav-item-wrapper ${activeSection === 'view' ? 'active' : ''}`}>
                <button
                  className="nav-item"
                  onClick={() => setActiveSection('view')}
                >
                  View
                </button>
                {hasViewSettingsChanged && (
                  <button 
                    className="set-default-btn-sidebar"
                    onClick={handleSetViewAsDefault}
                    disabled={isSavingDefault}
                    title="Save current view settings as default for this layer"
                  >
                    {isSavingDefault ? 'saving...' : 'set as default'}
                  </button>
                )}
              </div>
            </nav>
          </div>
          <div className="popup-content">
            {activeSection === 'marker' && (
              <MarkerLayerConfig layer={layer} settings={settings} />
            )}
            
            {activeSection === 'heatmap' && (
              <HeatmapLayerConfig layer={layer} settings={settings} />
            )}
            
            {activeSection === 'view' && (
              <ViewLayerConfig layer={layer} settings={layerConfig?.viewSettings} />
            )}
          </div>
        </div>
      </div>
    </Popup>
  );
};
